import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { UploadCloud, Users, Plus, CheckBig, Info } from "../../components/icons.js";
import { ResumeScoreList, type Scan } from "../users/ResumeScoreList.js";
import { competencyLabel, useVerticals } from "../users/useVerticals.js";

interface SourcingRequest {
  id: string;
  title: string;
  region: string;
  orgName: string;
  jobDescription: string | null;
  screenTarget: number | null;
  interviewTarget: number | null;
  finalistTarget: number | null;
  /** The vertical the requestor picked, and the competencyIds within it the desk must cover. */
  verticalKey: string;
  competencies: string[];
  scored: number;
  interviewing: number;
  createdAt: string;
}

type StatusFilter = "all" | "new" | "in-progress";

/** Derived queue status from the counts the API already returns (no schema/assignee needed). */
function statusOf(r: SourcingRequest): "new" | "in-progress" {
  return r.scored === 0 && r.interviewing === 0 ? "new" : "in-progress";
}

const SLA_DAYS = 3;
function ageDays(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}
function ageLabel(iso: string): string {
  const d = ageDays(iso);
  return d === 0 ? "today" : `${d}d in queue`;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const STAGES: { key: string; label: string }[] = [
  { key: "new", label: "New" },
  { key: "intake", label: "Intake" },
  { key: "posting", label: "Posting" },
  { key: "sourcing", label: "Sourcing" },
  { key: "interviewing", label: "Interviewing" },
  { key: "shortlist", label: "Shortlist" },
  { key: "closed", label: "Closed" },
];

interface DeskEngagement {
  id: string;
  requisitionId: string;
  stage: string;
  targetScans: number;
  targetInterviews: number;
  prepaidCredits: number;
  details: {
    salaryMin?: string; salaryMax?: string; location?: string; workMode?: string;
    notice?: string; skills?: string; benefits?: string[]; staffAug?: boolean;
  };
  channels: { k: string; posted: boolean }[];
  shortlist: string[];
  closeRequested: boolean;
  refundCredits: number | null;
  /** 0113: set when the desk switched the job to "I'll source" */
  convertedAt: string | null;
  used?: { scans: number; interviews: number; credits: number };
}

/** The 0031 staged desk for one engagement: stage strip + the action for where it stands.
 *  Intake and channels edit in place; close settles the honest refund (prepaid − used). */
function DeskPanel({ eng, onChanged }: { eng: DeskEngagement; onChanged: () => void }): ReactNode {
  const [details, setDetails] = useState(eng.details);
  const [channels, setChannels] = useState(eng.channels.length ? eng.channels : [
    { k: "Naukri", posted: false }, { k: "LinkedIn", posted: false },
    { k: "Instahyre", posted: false }, { k: "Our network", posted: false },
  ]);
  const [shortlistText, setShortlistText] = useState(eng.shortlist.join(", "));
  const [err, setErr] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (v: { path: string; method?: "PUT" | "POST"; body?: unknown }) =>
      v.method === "POST" ? api.post(v.path, v.body ?? {}) : api.put(v.path, v.body ?? {}),
    onSuccess: onChanged,
    onError: () => setErr("That desk action didn't save — check your role and retry."),
  });
  const now = STAGES.findIndex((x) => x.key === eng.stage);
  const used = eng.used ?? { scans: 0, interviews: 0, credits: 0 };
  const refund = Math.max(0, eng.prepaidCredits - used.credits);

  return (
    <div className="card pad" style={{ marginBottom: 14, background: "var(--surface-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {STAGES.map((st, i) => (
          <span key={st.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              className="pill"
              style={{
                background: i < now ? "var(--good-wash)" : i === now ? "var(--solid)" : "var(--surface)",
                color: i < now ? "var(--good)" : i === now ? "var(--rail-on)" : "var(--ink-mute)",
                border: "1px solid var(--line)",
              }}
            >
              {st.label}
            </span>
            {i < STAGES.length - 1 && <span style={{ color: "var(--ink-mute)" }}>›</span>}
          </span>
        ))}
        {eng.closeRequested && eng.stage !== "closed" && (
          <span className="pill r">client asked to close & settle</span>
        )}
        <span className="pill n tnum" style={{ marginLeft: "auto" }}>
          {used.credits} of {eng.prepaidCredits} cr used
        </span>
      </div>

      {err && <div className="banner crit" style={{ marginBottom: 10 }}><span>{err}</span></div>}

      {(eng.stage === "new" || eng.stage === "intake") && (
        <div style={{ display: "grid", gap: 8 }}>
          <div className="sm mut">
            <b>Intake:</b> collect what the sourcers post — the client's order pre-fills what they gave.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <input className="input" placeholder="Salary from" value={details.salaryMin ?? ""}
              onChange={(e) => setDetails({ ...details, salaryMin: e.target.value })} />
            <input className="input" placeholder="Salary to" value={details.salaryMax ?? ""}
              onChange={(e) => setDetails({ ...details, salaryMax: e.target.value })} />
            <input className="input" placeholder="Location" value={details.location ?? ""}
              onChange={(e) => setDetails({ ...details, location: e.target.value })} />
          </div>
          {Number(details.salaryMin) > 0 && Number(details.salaryMax) > 0 && Number(details.salaryMax) < Number(details.salaryMin) && (
            <p className="sm" role="alert" style={{ margin: "0", color: "var(--crit)" }}>
              Maximum salary must be greater than or equal to minimum salary.
            </p>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <select className="input" value={details.workMode ?? "Onsite"}
              onChange={(e) => setDetails({ ...details, workMode: e.target.value })}>
              {["Onsite", "Hybrid", "Remote"].map((m) => <option key={m}>{m}</option>)}
            </select>
            <input className="input" placeholder="Notice period" value={details.notice ?? ""}
              onChange={(e) => setDetails({ ...details, notice: e.target.value })} />
            <input className="input" placeholder="Must-have skills" value={details.skills ?? ""}
              onChange={(e) => setDetails({ ...details, skills: e.target.value })} />
          </div>
          <label className="sm" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={Boolean(details.staffAug)}
              onChange={(e) => setDetails({ ...details, staffAug: e.target.checked })} />
            Open to staff augmentation (hourly via Hasna Technologies MSA)
          </label>
          <div>
            <button className="btn sm primary" disabled={act.isPending || (Number(details.salaryMin) > 0 && Number(details.salaryMax) > 0 && Number(details.salaryMax) < Number(details.salaryMin))}
              onClick={() => act.mutate({ path: `/admin/sourcing-desk/${eng.id}/intake`, body: { details, advance: true } })}>
              Save intake → posting
            </button>
          </div>
        </div>
      )}

      {eng.stage === "posting" && (
        <div style={{ display: "grid", gap: 8 }}>
          <div className="sm mut"><b>Post the role</b> — tick each place the desk has posted, then go live.</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {channels.map((c, i) => (
              <label key={c.k} className="sm" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={c.posted}
                  onChange={(e) => setChannels(channels.map((x, j) => (j === i ? { ...x, posted: e.target.checked } : x)))} />
                {c.k}
              </label>
            ))}
          </div>
          <div>
            <button className="btn sm primary" disabled={act.isPending || !channels.some((c) => c.posted)}
              onClick={() => act.mutate({ path: `/admin/sourcing-desk/${eng.id}/channels`, body: { channels, goLive: true } })}>
              Live — start collecting resumes
            </button>
          </div>
        </div>
      )}

      {eng.stage === "sourcing" && (
        <div className="sm mut">
          Upload &amp; score below feeds the client live.{" "}
          <button className="btn sm" disabled={act.isPending}
            onClick={() => act.mutate({ path: `/admin/sourcing-desk/${eng.id}/stage`, body: { stage: "interviewing" } })}>
            Move to interviewing
          </button>
        </div>
      )}

      {(eng.stage === "interviewing" || eng.stage === "sourcing") && (
        <div style={{ display: "grid", gap: 8, marginTop: eng.stage === "sourcing" ? 8 : 0 }}>
          <div className="sm mut"><b>Shortlist</b> — the top {"3"} you recommend (comma-separated names):</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="input" style={{ flex: 1 }} placeholder="e.g. Kavya S., Dev P., Ananya G."
              value={shortlistText} onChange={(e) => setShortlistText(e.target.value)} />
            <button className="btn sm primary"
              disabled={act.isPending || shortlistText.split(",").filter((x) => x.trim()).length === 0}
              onClick={() => act.mutate({
                path: `/admin/sourcing-desk/${eng.id}/shortlist`,
                body: { shortlist: shortlistText.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 5) },
              })}>
              Share with the client
            </button>
          </div>
        </div>
      )}

      {eng.stage === "shortlist" && (
        <div className="sm" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span><b>Shared:</b> {eng.shortlist.join(", ") || "—"}</span>
          <button className="btn sm primary" disabled={act.isPending}
            onClick={() => act.mutate({ path: `/admin/sourcing-desk/${eng.id}/close`, method: "POST" })}>
            Close &amp; return {refund} unused cr
          </button>
        </div>
      )}
      {eng.closeRequested && eng.stage !== "closed" && eng.stage !== "shortlist" && (
        <div style={{ marginTop: 8 }}>
          <button className="btn sm primary" disabled={act.isPending}
            onClick={() => act.mutate({ path: `/admin/sourcing-desk/${eng.id}/close`, method: "POST" })}>
            Close now &amp; return {refund} unused cr
          </button>
        </div>
      )}
      {eng.stage === "closed" && (
        <div className="sm mut">Closed · {eng.refundCredits ?? 0} cr returned to the client.</div>
      )}

      {/* The client asks, the desk switches: settles like a close, then the client sources. */}
      {!eng.convertedAt && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="sm mut">
            Client wants to bring their own candidates?
          </span>
          <button className="btn sm ghost" disabled={act.isPending}
            onClick={() => {
              const settle = eng.stage === "closed"
                ? "The package is already settled."
                : `This closes the sourcing package and returns ${refund} unused cr to the client.`;
              if (!window.confirm(`Switch this job to "I'll source"? ${settle} The client can then add and invite candidates themselves. This can't be undone.`)) return;
              act.mutate({ path: `/admin/sourcing-desk/${eng.id}/convert`, method: "POST" });
            }}>
            Switch to I'll source
          </button>
        </div>
      )}
      {eng.convertedAt && (
        <div className="sm mut" style={{ marginTop: 8 }}>
          Switched to I'll source on {new Date(eng.convertedAt).toLocaleDateString()}.
        </div>
      )}
    </div>
  );
}

/** Owner · Sourcing queue (Mode A). Our team sees the requirement + targets, sources candidates
 *  externally, uploads their resumes here (AI-scored, no re-charge — client prepaid), and marks who
 *  to interview. Scores flow straight back to the client's requisition. */
export function Sourcing(): ReactNode {
  const qc = useQueryClient();
  // Labels for the competencyIds each request carries. Best-effort: if this desk operator can't
  // reach /verticals the ids still render, prettified, rather than blanking the row.
  const { byKey: verticalByKey } = useVerticals();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-sourcing"],
    queryFn: () => api.get<{ requests: SourcingRequest[] }>("/admin/sourcing"),
  });
  const deskQuery = useQuery({
    queryKey: ["admin-sourcing-desk"],
    queryFn: () => api.get<{ engagements: DeskEngagement[] }>("/admin/sourcing-desk"),
  });
  const engByReq = new Map((deskQuery.data?.engagements ?? []).map((e) => [e.requisitionId, e]));
  const requests = data?.requests ?? [];
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [newestFirst, setNewestFirst] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pending, setPending] = useState<File[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drag, setDrag] = useState(false);
  // Truthful outcome of the last "send to interview" — populated from what the server actually did
  // (some picks may already be invited and get skipped server-side; no-email candidates get no email).
  const [sendResult, setSendResult] = useState<{ requested: number; added: number; emailed: number } | null>(null);

  const openReq = requests.find((r) => r.id === openId) ?? null;

  const openRequest = (id: string): void => {
    setOpenId(id);
    setPending([]);
    setScans([]);
    setSelected(new Set());
    setSendResult(null);
    // load any resumes already scored for this request (admin cross-org endpoint)
    void api.get<{ scans: Scan[] }>(`/admin/requisitions/${id}/resumes`).then((r) => setScans(r.scans)).catch(() => {});
  };

  const score = useMutation({
    mutationFn: async (): Promise<Scan[]> => {
      const resumes = await Promise.all(
        pending.map(async (f) => ({ filename: f.name, mime: f.type || "application/octet-stream", contentBase64: await fileToDataUrl(f) })),
      );
      const res = await api.post<{ scans: Scan[] }>(`/admin/requisitions/${openId}/resumes`, { resumes });
      return res.scans;
    },
    onSuccess: (fresh) => {
      setScans((p) => [...p, ...fresh]);
      setSelected((prev) => {
        const n = new Set(prev);
        for (const s of fresh) if ((s.overall ?? 0) >= 70) n.add(s.id);
        return n;
      });
      setPending([]);
      void qc.invalidateQueries({ queryKey: ["admin-sourcing"] });
    },
  });

  const send = useMutation({
    // The server returns one row per candidate it ACTUALLY invited (`emailed` = whether an invite
    // email went out). Picks that already had a candidacy are skipped and never appear in `added`.
    mutationFn: (scanIds: string[]) =>
      api.post<{ added: { candidacyId: string; name: string; emailed: boolean; inviteUrl: string }[] }>(
        `/admin/requisitions/${openId}/select`,
        { scanIds },
      ),
    onSuccess: (res, scanIds) => {
      const emailed = res.added.filter((a) => a.emailed).length;
      setSendResult({ requested: scanIds.length, added: res.added.length, emailed });
      setSelected(new Set());
      // Re-load from the server so the list reflects the real state — no optimistic "all invited".
      void api.get<{ scans: Scan[] }>(`/admin/requisitions/${openId}/resumes`).then((r) => setScans(r.scans)).catch(() => {});
      void qc.invalidateQueries({ queryKey: ["admin-sourcing"] });
    },
  });

  const addFiles = (files: FileList | File[]): void =>
    setPending((prev) => {
      const merged = [...prev];
      for (const f of Array.from(files)) if (merged.length < 20 && !merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
      return merged;
    });
  const toggle = (id: string): void =>
    setSelected((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Sourcing queue</h1>
          <div className="sub">Mode A requests — source candidates, upload &amp; score their resumes, pick who to interview</div>
        </div>
        <span className="pill n">Owner team</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading the queue…</div>}
      {isError && (
        <div className="banner crit">
          <span>Couldn't load the sourcing queue. Please retry.</span>
        </div>
      )}

      {!isLoading && !isError && requests.length === 0 && (
        <div className="card pad" style={{ textAlign: "center" }}>
          <p className="mut" style={{ margin: 0 }}>No sourcing requests right now. New Mode A requests from clients appear here.</p>
        </div>
      )}

      {requests.length > 0 && (() => {
        const isClosed = (r: SourcingRequest) => engByReq.get(r.id)?.stage === "closed";
        const closedCount = requests.filter(isClosed).length;
        const listed = showClosed ? requests : requests.filter((r) => !isClosed(r));
        const counts = {
          all: listed.length,
          new: listed.filter((r) => statusOf(r) === "new").length,
          "in-progress": listed.filter((r) => statusOf(r) === "in-progress").length,
        };
        const visible = listed
          .filter((r) => statusFilter === "all" || statusOf(r) === statusFilter)
          .slice()
          .sort((a, b) => {
            const t = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            return newestFirst ? -t : t;
          });
        const FILTERS: { key: StatusFilter; label: string }[] = [
          { key: "all", label: "All" },
          { key: "new", label: "New" },
          { key: "in-progress", label: "In progress" },
        ];
        return (
        <>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
          <div className="seg">
            {FILTERS.map((f) => (
              <button key={f.key} className={statusFilter === f.key ? "on" : ""} onClick={() => setStatusFilter(f.key)}>
                {f.label} <span className="mut" style={{ fontWeight: 600 }}>{counts[f.key]}</span>
              </button>
            ))}
          </div>
          <label className="sm mut" style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            Show closed ({closedCount})
          </label>
          <div className="seg" style={{ marginLeft: "auto" }}>
            <button className={!newestFirst ? "on" : ""} onClick={() => setNewestFirst(false)}>Oldest</button>
            <button className={newestFirst ? "on" : ""} onClick={() => setNewestFirst(true)}>Newest</button>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="card pad" style={{ textAlign: "center" }}>
            <p className="mut" style={{ margin: 0 }}>No requests match this filter.</p>
          </div>
        ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visible.map((r) => {
            const isOpen = r.id === openId;
            const overSla = ageDays(r.createdAt) >= SLA_DAYS && statusOf(r) === "new" && !isClosed(r);
            return (
              <div key={r.id} className="card">
                <div className="card-h" style={{ cursor: "pointer" }} onClick={() => (isOpen ? setOpenId(null) : openRequest(r.id))}>
                  <div>
                    <h3 style={{ margin: 0 }}>{r.title}</h3>
                    <div className="mut sm" style={{ marginTop: 3 }}>
                      {r.orgName}{r.region ? ` · ${r.region}` : ""} · source {r.screenTarget ?? "—"} · interview {r.interviewTarget ?? "—"} · recommend {r.finalistTarget ?? "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span className={overSla ? "pill r" : "pill n"} title={new Date(r.createdAt).toLocaleString()}>{ageLabel(r.createdAt)}</span>
                    <span className="pill n" style={{ fontSize: 12 }}>
                      {verticalByKey(r.verticalKey)?.name ?? r.verticalKey.replace(/_/g, " ")}
                    </span>
                    {r.competencies.map((c) => (
                      <span key={c} className="pill t" style={{ fontSize: 12 }}>
                        {competencyLabel(verticalByKey(r.verticalKey), c)}
                      </span>
                    ))}
                    <span className={r.scored > 0 ? "pill v" : "pill n"}>{r.scored} scored</span>
                    {r.interviewing > 0 && <span className="pill pv">{r.interviewing} interviewing</span>}
                    {engByReq.get(r.id) && (
                      <span className={engByReq.get(r.id)!.closeRequested && engByReq.get(r.id)!.stage !== "closed" ? "pill r" : "pill pv"}>
                        {engByReq.get(r.id)!.closeRequested && engByReq.get(r.id)!.stage !== "closed"
                          ? "close requested"
                          : engByReq.get(r.id)!.stage}
                      </span>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="card-b">
                    {engByReq.get(r.id) && (
                      <DeskPanel
                        eng={engByReq.get(r.id)!}
                        onChanged={() => {
                          void qc.invalidateQueries({ queryKey: ["admin-sourcing-desk"] });
                          void qc.invalidateQueries({ queryKey: ["admin-sourcing"] });
                        }}
                      />
                    )}
                    {r.jobDescription && (
                      <div className="banner info" style={{ marginBottom: 14 }}>
                        <Info />
                        <div style={{ whiteSpace: "pre-wrap", fontSize: 12.5 }}>{r.jobDescription.slice(0, 1200)}</div>
                      </div>
                    )}
                    <p className="sm mut" style={{ marginTop: 0 }}>
                      Source candidates on Naukri / LinkedIn / your channels, then drop their resumes here — each is
                      scored against the requirement. Then tick who to interview.
                    </p>

                    <div
                      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={() => setDrag(false)}
                      onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files); }}
                      onClick={() => document.getElementById(`src-input-${r.id}`)?.click()}
                      style={{ border: `1.5px dashed ${drag ? "var(--teal)" : "var(--line)"}`, borderRadius: 12, background: drag ? "var(--teal-wash)" : "var(--surface-2)", padding: "22px 16px", textAlign: "center", cursor: "pointer" }}
                    >
                      <UploadCloud width={22} height={22} style={{ color: "var(--teal-ink)" }} />
                      <div className="mut sm" style={{ marginTop: 6 }}><b style={{ color: "var(--teal-ink)" }}>Drop sourced resumes</b> or click to browse · PDF / DOC / TXT</div>
                      <input id={`src-input-${r.id}`} type="file" accept=".pdf,.doc,.docx,.txt,.md,application/pdf,text/plain" multiple hidden onChange={(e) => e.target.files && addFiles(e.target.files)} />
                    </div>
                    {pending.length > 0 && (
                      <>
                        <p className="mut sm" style={{ margin: "10px 0 6px" }}>{pending.length} file{pending.length === 1 ? "" : "s"} ready</p>
                        <button className="btn primary" disabled={score.isPending} onClick={() => score.mutate()}>
                          <Plus /> {score.isPending ? "Scoring…" : `Score ${pending.length}`}
                        </button>
                      </>
                    )}

                    {scans.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                          <b>Scored candidates</b>
                          <span className="mut sm">{selected.size}/{scans.length} selected</span>
                        </div>
                        <ResumeScoreList scans={scans} selected={selected} onToggle={toggle} disabled={send.isPending} />
                        <button className="btn primary" style={{ marginTop: 14 }} disabled={send.isPending || selected.size === 0} onClick={() => send.mutate([...selected])}>
                          <CheckBig /> {send.isPending ? "Sending…" : `Send ${selected.size} to interview`}
                        </button>
                        {send.isSuccess && sendResult && (
                          <div className="banner info" style={{ marginTop: 12 }}>
                            <Users />
                            <div>
                              <b>
                                {sendResult.added} invited · {sendResult.emailed} emailed
                                {sendResult.requested > sendResult.added
                                  ? ` · ${sendResult.requested - sendResult.added} skipped (already invited)`
                                  : ""}
                              </b>
                              {sendResult.added > sendResult.emailed && (
                                <div className="mut sm" style={{ marginTop: 4 }}>
                                  {sendResult.added - sendResult.emailed} candidate
                                  {sendResult.added - sendResult.emailed === 1 ? " has" : "s have"} no email on file — no invite
                                  email was sent; reach them with their saved interview link.
                                </div>
                              )}
                              {sendResult.added > 0 && (
                                <div className="mut sm" style={{ marginTop: 4 }}>
                                  The client sees the invited candidates in their requisition.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
        </>
        );
      })()}
    </section>
  );
}
